import React, { 
  useMemo, 
  useState, 
  useCallback 
} from "react";

import CampaignCreationModal from "../../campaigns/CampaignCreationModal";
import { 
  Button, 
  Container, 
  FormEntry, 
  ScreenHeader 
} from "../../wombat-ui";
import { 
  ArrowRightIcon, 
  ReviewIcon, 
  PlusIcon 
} from "../../icons";

import { FormStepProps } from "../../../screens/ReviewFormScreen";
import { 
  Controller, 
  FieldValues, 
  useForm, 
  useWatch, 
  Control, 
} from "react-hook-form";
import useTranslations from "../../../hooks/useTranslations";
import { 
  StringParam, 
  useQueryParams 
} from "use-query-params";
import {
  GetUsersMinimalDocument,
  UserMinimalFragment,
} from "../../../graphql/generated/definitions";
import AsyncSelect from "../../utils/chakra/AsyncSelect";
import { Select } from "chakra-react-select";

import { useApolloClient } from "@apollo/client";
import useViewer from "../../../hooks/useViewer";

type NextStepButtonControlProps = {
  formState: ReturnType<typeof useForm>["formState"];
  onNext: () => void;
  control: Control<FieldValues>;
};

const NextStepButtonControl: React.FC<NextStepButtonControlProps> = React.memo(
  ({ formState, onNext, control }) => {
    const t = useTranslations();

    const campaignId = useWatch({
      control,
      name: "campaignId"
    });
    
    const referentIds = useWatch({
      control,
      name: "referentIds"
    });

    const collaboratorIds = useWatch({
      control,
      name: "collaboratorIds"
    });

    const disabled = useMemo(() => {
      return (
        formState.errors.campaignId !== undefined ||
        formState.errors.referentIds !== undefined ||
        formState.errors.collaboratorIds !== undefined ||
        !campaignId ||
        !referentIds ||
        !collaboratorIds
      );
    }, [formState, campaignId, referentIds, collaboratorIds]);

    return (
      <Button
        disabled={disabled}
        onClick={onNext}
        id="nextStep"
        trailingIcon={ArrowRightIcon}
      >
        {t("forms.actions.next")}
      </Button>
    );
  }
);

type MissionType = "available" | "filled";
const FirstStep: React.FC<FormStepProps> = ({
  onNext,
  formState,
  control,
}) => {
  const client = useApolloClient();

  const [innerStep] = useState<MissionType>("filled");
  const [userModalToggled, setUserModalToggled] = useState(false);
  const t = useTranslations();

  const { company } = useViewer();

  // Preset le champ dans la select de Campaign
  const [{ campaignId }] = useQueryParams({
    campaignId: StringParam,
  });

  const campaignsOptions = useMemo(() => {
    if(company) {
      return company.campaigns.map((campaign) => 
        ({value: campaign.id, label: campaign.name})
      )
    }

    return [];
  }, 
  [company]);

  const defaultCampaignOption = useMemo(() => {
    if (campaignId)
      return campaignsOptions.find(({ value }) => value === campaignId)

    return null;
  }, 
  [campaignId, campaignsOptions]);

  const getUsers = useCallback(
    async (variables) => {
      const usersData = await client.query({
        query: GetUsersMinimalDocument,
        variables
      });

      return (
        usersData.data?.users.collection as UserMinimalFragment[]).map(({ name: label, id: value }) => (
          { label, value }
        )
      );
    },
    [client]
  );

  const getAllUsers = useCallback(() => (
    getUsers({ q: "" })
  ), [getUsers]);

  const searchUsers = useCallback((q) => (
    getUsers({ q, per: 20 })
  ), [getUsers]);

  return (
    <>
      <ScreenHeader
        icon={ReviewIcon}
        title={t("reviews.new")}
        description={t("reviews.whoPartakes")}
        actions={[
          <NextStepButtonControl
            onNext={onNext}
            formState={formState}
            key="next"
            control={control}
          />,
        ]}
      />
      <Container
        className="flex flex-col flex-grow w-full text-primary-900"
        id="first-step-container"
      >
        {innerStep === "filled" && (
          <>
            <div>
              <h3 className="text-xl font-medium text-primary-900">
                {t("reviews.chooseParticipants")}
              </h3>
            </div>
            <CampaignCreationModal
              isOpen={userModalToggled}
              onClose={() => {
                setUserModalToggled(false);

                // Submitting the campaign creation form will cause the review creation form
                // to set the isSubmitted flag to true (which is probably a bug in RHF).
                // Since we need this flag to avoid multiple creation we must reset the
                // entire form to reset the isSubmitted flag.

                //UPDATE : this line break the new system with Controllers
                //reset(getValues());
              }}
            />
          </>
        )}
        <div className="mt-5">
          <div className="grid grid-cols-6 gap-y-4">
            {innerStep === "filled" && (
              <div className="col-span-6 sm:col-span-4">
                <FormEntry title={t("reviews.chooseCollaborators")} required>
                  <Controller
                    control={control}
                    name="collaboratorIds"
                    defaultValue={[]}
                    rules={{ validate: (data) => data.length > 0 }}
                    render={({ onChange, ref, onBlur }) => (
                      <AsyncSelect
                        id="select-mission-users"
                        inputRef={ref}
                        isMultiple
                        placeholder={t("reviews.searchCollaborators") as string}
                        options={searchUsers}
                        onChange={(value) => {
                          onChange(value.map((item) => item.value));
                        }}
                        onBlur={onBlur}
                        allOptions={getAllUsers}
                        canSelectAll
                        selectAllLabel={
                          t("reviews.selectAllCollaborators") as string
                        }
                      />
                    )}
                  />
                </FormEntry>
              </div>
            )}
            {innerStep === "filled" && (
              <div className="col-span-6 sm:col-span-4">
                <FormEntry title={t("reviews.chooseReferents")}>
                  <Controller
                    control={control}
                    name="referentIds"
                    defaultValue={[]}
                    rules={{ required: false }}
                    render={({ onChange, ref, onBlur }) => (
                      <AsyncSelect
                        id="select-mission-referents"
                        inputRef={ref}
                        isMultiple
                        placeholder={t("reviews.searchReferents") as string}
                        options={searchUsers}
                        onChange={(value) => {
                          onChange(value.map((item) => item.value))
                        }}
                        onBlur={onBlur}
                        allOptions={getAllUsers}
                        canSelectAll
                        selectAllLabel={
                          t("reviews.selectAllReferents") as string
                        }
                      />
                    )}
                  />
                </FormEntry>
              </div>
            )}
            <div className="col-span-6 sm:col-span-4">
              <FormEntry title={t("reviews.chooseHS")} required>
                <Controller
                  control={control}
                  name="campaignId"
                  defaultValue={defaultCampaignOption?.value}
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <Select
                      id="select-mission-campaign"
                      searchable
                      defaultValue={defaultCampaignOption}
                      placeholder={t("reviews.selectHS") as string}
                      options={campaignsOptions}
                      onChange={(item) => {
                        onChange(item.value); 
                      }}
                    />
                  )}
                />

                <Button
                  className="-mt-2"
                  variant="link"
                  scale="sm"
                  leadingIcon={PlusIcon}
                  onClick={() => setUserModalToggled(true)}
                  id="addCampaign"
                >
                  {t("reviews.createCampaign")}
                </Button>
              </FormEntry>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default React.memo(FirstStep);
